'use client';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash } from '@phosphor-icons/react';
import { ColumnSelector } from './ColumnSelector';
import type { Column } from '../../types';

type TimeGranularity = 'raw' | 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

export interface Condition {
  column: string;
  operator: string;
  value: string;
  valueType?: string;
  logic?: 'AND' | 'OR';
  timeGranularity?: TimeGranularity;
}

interface OperatorDef {
  value: string;
  labelKey: string;
  types: string[];
  hideValue?: boolean;
  region?: 'cn';
}

const OPERATORS: OperatorDef[] = [
  { value: 'eq', labelKey: 'eq', types: ['string', 'number', 'date', 'time', 'boolean', 'enum'] },
  { value: 'ne', labelKey: 'ne', types: ['string', 'number', 'date', 'time', 'boolean', 'enum'] },
  { value: 'gt', labelKey: 'gt', types: ['number', 'date', 'time'] },
  { value: 'lt', labelKey: 'lt', types: ['number', 'date', 'time'] },
  { value: 'gte', labelKey: 'gte', types: ['number', 'date', 'time'] },
  { value: 'lte', labelKey: 'lte', types: ['number', 'date', 'time'] },
  { value: 'contains', labelKey: 'contains', types: ['string'] },
  { value: 'starts_with', labelKey: 'starts_with', types: ['string'] },
  { value: 'ends_with', labelKey: 'ends_with', types: ['string'] },
  { value: 'regex_match', labelKey: 'regex_match', types: ['string'] },
  { value: 'is_digits', labelKey: 'is_digits', types: ['string'], hideValue: true },
  { value: 'is_alpha', labelKey: 'is_alpha', types: ['string'], hideValue: true },
  { value: 'has_digits', labelKey: 'has_digits', types: ['string'], hideValue: true },
  { value: 'has_date', labelKey: 'has_date', types: ['string'], hideValue: true },
  { value: 'is_email', labelKey: 'is_email', types: ['string'], hideValue: true },
  { value: 'is_url', labelKey: 'is_url', types: ['string'], hideValue: true },
  { value: 'is_ip', labelKey: 'is_ip', types: ['string'], hideValue: true },
  { value: 'is_phone', labelKey: 'is_phone', types: ['string'], hideValue: true, region: 'cn' },
  { value: 'is_id_card', labelKey: 'is_id_card', types: ['string'], hideValue: true, region: 'cn' },
  { value: 'is_null', labelKey: 'is_null', types: ['string', 'number', 'date', 'time', 'boolean', 'enum'], hideValue: true },
  { value: 'is_not_null', labelKey: 'is_not_null', types: ['string', 'number', 'date', 'time', 'boolean', 'enum'], hideValue: true },
];

const APP_REGION = import.meta.env.VITE_APP_REGION || 'global';

const SELECTABLE_TYPES = ['string', 'number', 'date', 'time', 'boolean'] as const;

const TYPE_BADGE_COLORS: Record<string, string> = {
  string: 'bg-blue-50 text-blue-600 border-blue-200',
  number: 'bg-amber-50 text-amber-600 border-amber-200',
  date: 'bg-purple-50 text-purple-600 border-purple-200',
  time: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  boolean: 'bg-green-50 text-green-600 border-green-200',
  enum: 'bg-rose-50 text-rose-600 border-rose-200',
};

const DATE_GRANULARITY_OPTIONS: { value: TimeGranularity; labelKey: string }[] = [
  { value: 'raw', labelKey: 'nodes.config.condition_builder.timeRaw' },
  { value: 'year', labelKey: 'nodes.config.condition_builder.timeYear' },
  { value: 'month', labelKey: 'nodes.config.condition_builder.timeMonth' },
  { value: 'day', labelKey: 'nodes.config.condition_builder.timeDay' },
];

const TIME_GRANULARITY_OPTIONS: { value: TimeGranularity; labelKey: string }[] = [
  { value: 'raw', labelKey: 'nodes.config.condition_builder.timeRaw' },
  { value: 'hour', labelKey: 'nodes.config.condition_builder.timeHour' },
  { value: 'minute', labelKey: 'nodes.config.condition_builder.timeMinute' },
  { value: 'second', labelKey: 'nodes.config.condition_builder.timeSecond' },
];



function normalizeColumnType(type: string | undefined): string {
  if (!type) return 'string';
  const t = type.toLowerCase();
  if (['int', 'float', 'double', 'decimal', 'bigint'].includes(t)) return 'number';
  if (['datetime', 'timestamp'].includes(t)) return 'date';
  if (t === 'time') return 'time';
  return t;
}

function getOperatorsForType(columnType: string): OperatorDef[] {
  return OPERATORS.filter(op => {
    if (!op.types.includes(columnType)) return false;
    if (op.region && op.region !== APP_REGION) return false;
    return true;
  });
}

function isTemporalType(columnType: string): boolean {
  return columnType === 'date' || columnType === 'time';
}

function getTypeLabelKey(type: string): string {
  const map: Record<string, string> = {
    string: 'nodes.config.condition_builder.typeString',
    number: 'nodes.config.condition_builder.typeNumber',
    date: 'nodes.config.condition_builder.typeDate',
    time: 'nodes.config.condition_builder.typeTime',
    boolean: 'nodes.config.condition_builder.typeBoolean',
    enum: 'nodes.config.condition_builder.typeEnum',
  };
  return map[type] ?? 'nodes.config.condition_builder.typeUnknown';
}

function getEffectiveType(condition: Condition, schemaType: string | undefined): string {
  if (condition.valueType) return condition.valueType;
  return normalizeColumnType(schemaType);
}

function getGranularityPlaceholderKey(granularity: TimeGranularity): string {
  const map: Record<string, string> = {
    year: 'nodes.config.condition_builder.yearPlaceholder',
    month: 'nodes.config.condition_builder.monthPlaceholder',
    day: 'nodes.config.condition_builder.dayPlaceholder',
    hour: 'nodes.config.condition_builder.hourPlaceholder',
    minute: 'nodes.config.condition_builder.minutePlaceholder',
    second: 'nodes.config.condition_builder.secondPlaceholder',
  };
  return map[granularity] ?? 'nodes.config.condition_builder.valuePlaceholder';
}

export interface ConditionBuilderProps {
  conditions: Condition[];
  onChange: (conditions: Condition[]) => void;
  columns: Array<{ column: Column; source: { nodeId: string; nodeType: string; nodeName: string } }> | null | undefined;
  showLogic?: boolean;
  showTimeGranularity?: boolean;
  title?: string;
}

export function ConditionBuilder({
  conditions,
  onChange,
  columns,
  showLogic = true,
  showTimeGranularity = true,
  title,
}: ConditionBuilderProps) {
  const { t } = useTranslation();
  const prefix = 'nodes.config.condition_builder';

  const getColumnType = useCallback(
    (columnName: string): string | undefined => {
      if (!columns) return undefined;
      const col = columns.find(c => c.column.name === columnName);
      return col?.column.type;
    },
    [columns],
  );

  const updateCondition = useCallback(
    (index: number, updates: Partial<Condition>) => {
      const current = conditions[index];
      const merged = { ...updates };

      if (updates.column !== undefined && updates.column !== current.column) {
        const colType = getColumnType(updates.column);
        const normalized = normalizeColumnType(colType);
        merged.valueType = normalized;

        const available = getOperatorsForType(normalized);
        if (!available.some(op => op.value === current.operator)) {
          merged.operator = available[0]?.value ?? 'eq';
        }

        if (!isTemporalType(normalized)) {
          merged.timeGranularity = undefined;
        } else if (!current.timeGranularity) {
          merged.timeGranularity = 'raw';
        }
      }

      if (updates.valueType !== undefined && updates.valueType !== current.valueType) {
        const available = getOperatorsForType(updates.valueType);
        if (!available.some(op => op.value === (merged.operator ?? current.operator))) {
          merged.operator = available[0]?.value ?? 'eq';
        }
        if (!isTemporalType(updates.valueType)) {
          merged.timeGranularity = undefined;
        } else if (!current.timeGranularity) {
          merged.timeGranularity = 'raw';
        }
        if (updates.valueType === 'boolean') {
          merged.value = merged.value ?? current.value;
          if (merged.value !== 'true' && merged.value !== 'false') {
            merged.value = 'true';
          }
        }
      }

      const next = conditions.map((c, i) => (i === index ? { ...c, ...merged } : c));
      onChange(next);
    },
    [conditions, onChange, getColumnType],
  );

  const addCondition = useCallback(() => {
    onChange([
      ...conditions,
      { column: '', operator: 'eq', value: '', valueType: 'string', logic: 'AND', timeGranularity: undefined },
    ]);
  }, [conditions, onChange]);

  const removeCondition = useCallback(
    (index: number) => {
      onChange(conditions.filter((_, i) => i !== index));
    },
    [conditions, onChange],
  );

  const operatorHidesValue = (operatorValue: string): boolean => {
    return OPERATORS.find(op => op.value === operatorValue)?.hideValue === true;
  };

  const getValuePlaceholder = (effectiveType: string, granularity?: TimeGranularity, operator?: string): string => {
    if (operator === 'regex_match') return t(`${prefix}.regexPlaceholder`);
    if (granularity && granularity !== 'raw') {
      return t(getGranularityPlaceholderKey(granularity));
    }
    if (effectiveType === 'number') return t(`${prefix}.numberPlaceholder`);
    if (effectiveType === 'date') return t(`${prefix}.datePlaceholder`);
    if (effectiveType === 'time') return t(`${prefix}.timePlaceholder`);
    return t(`${prefix}.valuePlaceholder`);
  };

  return (
    <div className="space-y-4">
      {title && (
        <div className="text-sm font-medium text-zinc-700">{title}</div>
      )}

      <AnimatePresence>
        {conditions.map((condition, index) => {
          const schemaType = getColumnType(condition.column);
          const effectiveType = getEffectiveType(condition, schemaType);
          const availableOps = getOperatorsForType(effectiveType);
          const typeBadgeColor = TYPE_BADGE_COLORS[effectiveType] ?? TYPE_BADGE_COLORS.string;
          const granularityOptions = effectiveType === 'time' ? TIME_GRANULARITY_OPTIONS : DATE_GRANULARITY_OPTIONS;
          const hasGranularity = condition.timeGranularity && condition.timeGranularity !== 'raw';

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 space-y-3"
            >
              {showLogic && index > 0 && (
                <div className="flex justify-end">
                  <select
                    value={condition.logic ?? 'AND'}
                    onChange={(e) => updateCondition(index, { logic: e.target.value as 'AND' | 'OR' })}
                    className="text-xs font-medium bg-white border border-zinc-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="AND">{t(`${prefix}.and`)}</option>
                    <option value="OR">{t(`${prefix}.or`)}</option>
                  </select>
                </div>
              )}

              <div className="space-y-3">
                {/* Column Selector + Type Selector */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-zinc-500">
                      {t(`${prefix}.selectColumn`)}
                    </label>
                    {condition.column && (
                      <select
                        value={effectiveType}
                        onChange={(e) => updateCondition(index, { valueType: e.target.value })}
                        className={`text-[10px] font-medium rounded-md border px-1.5 py-0.5 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-500/30 ${typeBadgeColor}`}
                      >
                        {SELECTABLE_TYPES.map((tp) => (
                          <option key={tp} value={tp}>
                            {t(getTypeLabelKey(tp))}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <ColumnSelector
                    value={condition.column}
                    onChange={(columnName) => updateCondition(index, { column: columnName })}
                    placeholder={columns ? t(`${prefix}.selectColumn`) : t(`${prefix}.connectInputFirst`)}
                    columns={columns ?? []}
                  />
                </div>

                {/* Time Granularity — only for date / time types */}
                {showTimeGranularity && isTemporalType(effectiveType) && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-zinc-500">
                      {t(`${prefix}.timeGranularity`)}
                    </label>
                    <select
                      value={condition.timeGranularity ?? 'raw'}
                      onChange={(e) => updateCondition(index, { timeGranularity: e.target.value as TimeGranularity })}
                      className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    >
                      {granularityOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-zinc-400">{t(`${prefix}.timeGranularityHint`)}</p>
                  </div>
                )}



                {/* Operator */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-500">
                    {t(`${prefix}.operator`)}
                  </label>
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(index, { operator: e.target.value })}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    {availableOps.map((op) => (
                      <option key={op.value} value={op.value}>
                        {t(`nodes.operators.${op.labelKey}`)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Value Input — varies by type */}
                {!operatorHidesValue(condition.operator) && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-zinc-500">
                      {t(`${prefix}.value`)}
                    </label>

                    {effectiveType === 'boolean' ? (
                      <select
                        value={condition.value || 'true'}
                        onChange={(e) => updateCondition(index, { value: e.target.value })}
                        className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                      >
                        <option value="true">{t(`${prefix}.boolTrue`)}</option>
                        <option value="false">{t(`${prefix}.boolFalse`)}</option>
                      </select>
                    ) : effectiveType === 'number' && !hasGranularity ? (
                      <input
                        type="number"
                        placeholder={getValuePlaceholder(effectiveType)}
                        value={condition.value}
                        onChange={(e) => updateCondition(index, { value: e.target.value })}
                        className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                      />
                    ) : (
                      <input
                        type="text"
                        placeholder={getValuePlaceholder(effectiveType, condition.timeGranularity, condition.operator)}
                        value={condition.value}
                        onChange={(e) => updateCondition(index, { value: e.target.value })}
                        className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                      />
                    )}
                  </div>
                )}
              </div>

              {conditions.length > 1 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => removeCondition(index)}
                  className="flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                >
                  <Trash className="w-3.5 h-3.5" />
                  {t(`${prefix}.deleteCondition`)}
                </motion.button>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={addCondition}
        className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
      >
        <Plus className="w-4 h-4" />
        {t(`${prefix}.addCondition`)}
      </motion.button>
    </div>
  );
}
